import { useState, type FormEvent } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Pagination,
  Skeleton,
  Stack,
  Switch,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import KeyRounded from '@mui/icons-material/KeyRounded';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import PersonAddAlt1Rounded from '@mui/icons-material/PersonAddAlt1Rounded';
import QueryStatsRounded from '@mui/icons-material/QueryStatsRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import { useNavigate } from 'react-router';
import { ApiError } from '@/api';
import { CsvImportDialog } from '@/components/data/CsvImportDialog';
import { EmptyState } from '@/components/EmptyState';
import { useConfirm } from '@/components/feedback/confirmContext';
import { useNotify } from '@/components/feedback/notificationsContext';
import { PasswordField } from '@/components/form/PasswordField';
import { UserAvatar } from '@/components/UserAvatar';
import { useAsync } from '@/hooks/useAsync';
import { useDebounced } from '@/hooks/useDebounced';
import {
  createUser,
  deleteUser,
  listCourses,
  listGroups,
  listUsers,
  setUserPassword,
  updateUser,
  type UserSort,
} from '@/platform/api';
import { useSession } from '@/platform/session';
import { startViewAs } from '@/platform/viewAs';
import {
  ROLE_LABEL,
  type CourseRef,
  type Group,
  type InstitutionRef,
  type Person,
  type Role,
} from '@/platform/types';
import { formatRelative } from '@/utils/format';
import { PageHeader } from '../PageHeader';
import { PeopleTabs } from './PeopleTabs';

const PAGE_SIZE = 20;
/** Header and rows share these columns: avatar, name, role, groups, last access, actions. */
const LIST_COLUMNS = '36px minmax(0, 2fr) 130px minmax(0, 1.4fr) 110px 40px';
const SORT_COLUMNS: { key: UserSort; label: string; first: 'asc' | 'desc' }[] = [
  { key: 'name', label: 'Nome', first: 'asc' },
  { key: 'role', label: 'Perfil', first: 'asc' },
  { key: 'groups', label: 'Turmas', first: 'asc' },
  // The latest access is what people look for first.
  { key: 'lastLogin', label: 'Acesso', first: 'desc' },
];
const ROLE_COLOR: Record<Role, 'primary' | 'info' | 'default'> = {
  admin: 'primary',
  coordinator: 'primary',
  teacher: 'info',
  student: 'default',
  reviewer: 'info',
};

/** Who may create/edit whom (mirrors the API): coordinators run teachers and students. */
const MANAGEABLE: Record<Role, Role[]> = {
  admin: ['student', 'teacher', 'coordinator', 'admin'],
  coordinator: ['student', 'teacher'],
  teacher: ['student'],
  student: [],
  // Reviewers are invited in Administração › Revisores, not created here.
  reviewer: [],
};

export function UsersPage() {
  const { isAdmin, isManager, hasModule, user: me, institution } = useSession();
  const navigate = useNavigate();
  const notify = useNotify();
  const confirm = useConfirm();
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<Role | ''>('');
  const [groupId, setGroupId] = useState('');
  const [courseId, setCourseId] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive' | ''>('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ key: UserSort; order: 'asc' | 'desc' }>({
    key: 'name',
    order: 'asc',
  });
  const [editing, setEditing] = useState<Person | 'new' | null>(null);
  const [passwordFor, setPasswordFor] = useState<Person | null>(null);
  const [importing, setImporting] = useState(false);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; person: Person } | null>(null);
  const q = useDebounced(search);

  const groups = useAsync((signal) => listGroups(false, signal));
  const courses = useAsync((signal) => listCourses(false, signal));
  const users = useAsync(
    (signal) =>
      listUsers(
        {
          search: q || undefined,
          role,
          groupId: groupId || undefined,
          courseId: courseId || undefined,
          status,
          sort: sort.key,
          order: sort.order,
          page,
          pageSize: PAGE_SIZE,
        },
        signal,
      ),
    [q, role, groupId, courseId, status, sort, page],
  );
  const data = users.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const sortBy = (column: (typeof SORT_COLUMNS)[number]) => {
    const flipped = sort.order === 'asc' ? 'desc' : 'asc';
    setSort({ key: column.key, order: sort.key === column.key ? flipped : column.first });
    setPage(1);
  };

  const toggleActive = async (person: Person) => {
    await updateUser(person.id, { isActive: !person.isActive });
    notify(person.isActive ? 'Acesso desativado.' : 'Acesso reativado.', 'success');
    users.reload();
  };

  const remove = async (person: Person) => {
    const ok = await confirm({
      title: `Excluir ${person.name ?? person.email}?`,
      description:
        'Todas as respostas e notas desta pessoa também serão apagadas. Prefira desativar o acesso para manter o histórico.',
      confirmLabel: 'Excluir definitivamente',
      danger: true,
    });
    if (!ok) return;
    await deleteUser(person.id);
    notify('Pessoa excluída.', 'success');
    users.reload();
  };

  return (
    <>
      <PageHeader
        title="Pessoas"
        subtitle={
          data
            ? `${data.total.toLocaleString('pt-BR')} ${data.total === 1 ? 'pessoa' : 'pessoas'}`
            : 'Carregando…'
        }
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<UploadFileRounded />}
              onClick={() => setImporting(true)}
            >
              Importar planilha
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddAlt1Rounded />}
              onClick={() => setEditing('new')}
            >
              Nova pessoa
            </Button>
          </>
        }
      />
      <PeopleTabs />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '2fr 1fr 1.4fr 1.4fr 1fr' },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <TextField
          size="small"
          placeholder="Nome, e-mail ou matrícula"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded fontSize="small" />
                </InputAdornment>
              ),
            },
            htmlInput: { 'aria-label': 'Buscar pessoas' },
          }}
        />
        <TextField
          select
          size="small"
          label="Perfil"
          value={role}
          onChange={(e) => {
            setRole(e.target.value as Role | '');
            setPage(1);
          }}
        >
          <MenuItem value="">Todos</MenuItem>
          {(Object.keys(ROLE_LABEL) as Role[]).map((r) => (
            <MenuItem key={r} value={r}>
              {ROLE_LABEL[r]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Turma"
          value={groupId}
          onChange={(e) => {
            setGroupId(e.target.value);
            setPage(1);
          }}
        >
          <MenuItem value="">Todas</MenuItem>
          {(groups.data ?? []).map((g) => (
            <MenuItem key={g.id} value={g.id}>
              {g.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Curso"
          value={courseId}
          onChange={(e) => {
            setCourseId(e.target.value);
            setPage(1);
          }}
        >
          <MenuItem value="">Todos</MenuItem>
          {(courses.data ?? []).map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Acesso"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as typeof status);
            setPage(1);
          }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="active">Ativos</MenuItem>
          <MenuItem value="inactive">Desativados</MenuItem>
        </TextField>
      </Box>

      {users.error ? (
        <Alert severity="error">Não foi possível carregar as pessoas.</Alert>
      ) : (
        <Card>
          {users.loading && !data ? (
            [0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={52} sx={{ m: 2 }} />
            ))
          ) : data?.items.length === 0 ? (
            <EmptyState
              compact
              icon={GroupsRounded}
              title="Ninguém encontrado"
              description="Cadastre pessoas uma a uma ou importe a lista da turma por CSV."
            />
          ) : (
            <>
              <Box
                sx={(t) => ({
                  display: { xs: 'none', md: 'grid' },
                  gridTemplateColumns: LIST_COLUMNS,
                  gap: 2,
                  px: 2.5,
                  py: 1,
                  alignItems: 'center',
                  borderBottom: `1px solid ${t.vars.palette.divider}`,
                })}
              >
                {SORT_COLUMNS.map((column, index) => (
                  <Typography
                    key={column.key}
                    variant="caption"
                    component="div"
                    color="text.secondary"
                    aria-sort={
                      sort.key !== column.key
                        ? undefined
                        : sort.order === 'asc'
                          ? 'ascending'
                          : 'descending'
                    }
                    sx={{ gridColumn: index === 0 ? '1 / 3' : index + 2 }}
                  >
                    <TableSortLabel
                      active={sort.key === column.key}
                      direction={sort.key === column.key ? sort.order : column.first}
                      onClick={() => sortBy(column)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </Typography>
                ))}
              </Box>
              {data?.items.map((person) => (
                <Box
                  key={person.id}
                  sx={(t) => ({
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'auto 1fr auto',
                      md: LIST_COLUMNS,
                    },
                    gap: { xs: 1.5, md: 2 },
                    px: { xs: 2, sm: 2.5 },
                    py: 1.5,
                    alignItems: 'center',
                    opacity: person.isActive ? 1 : 0.55,
                    '& + &': { borderTop: `1px solid ${t.vars.palette.divider}` },
                  })}
                >
                  <UserAvatar
                    user={{
                      ...person,
                      emailVerified: person.emailVerified,
                      signInMethod: 'unknown',
                    }}
                    sx={{ width: 36, height: 36 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>
                      {person.name ?? '—'}
                      {!person.isActive && ' (desativado)'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {person.email}
                      {person.registration ? ` · ${person.registration}` : ''}
                    </Typography>
                    {(person.institutions.length > 0 || person.course) && (
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {[person.institutions.map((i) => i.name).join(', '), person.course?.name]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Chip
                      size="small"
                      variant="soft"
                      color={ROLE_COLOR[person.role]}
                      label={ROLE_LABEL[person.role]}
                    />
                    {person.requestedRole && (
                      <Chip
                        size="small"
                        variant="soft"
                        color="warning"
                        label="Aguarda aprovação"
                        title={`Pediu acesso como ${ROLE_LABEL[person.requestedRole].toLowerCase()} (Aprovações)`}
                        sx={{ ml: 0.5 }}
                      />
                    )}
                  </Box>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    useFlexGap
                    sx={{ display: { xs: 'none', md: 'flex' }, flexWrap: 'wrap' }}
                  >
                    {person.groups.map((g) => (
                      <Chip
                        key={g.id}
                        size="small"
                        variant="outlined"
                        label={g.code ?? g.name}
                        title={g.name}
                      />
                    ))}
                  </Stack>
                  <Tooltip
                    title={
                      person.hasGoogle
                        ? 'Entra com Google'
                        : person.hasPassword
                          ? 'Entra com senha'
                          : 'Ainda sem forma de acesso: defina uma senha ou peça login com Google'
                    }
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: { xs: 'none', md: 'block' } }}
                    >
                      {person.lastLoginAt
                        ? `acesso ${formatRelative(person.lastLoginAt)}`
                        : 'nunca acessou'}
                    </Typography>
                  </Tooltip>
                  <IconButton
                    aria-label={`Ações para ${person.name ?? person.email}`}
                    onClick={(e) => setMenu({ anchor: e.currentTarget, person })}
                  >
                    <MoreVertRounded />
                  </IconButton>
                </Box>
              ))}
            </>
          )}
        </Card>
      )}
      {pages > 1 && (
        <Stack sx={{ alignItems: 'center', mt: 3 }}>
          <Pagination
            count={pages}
            page={page}
            onChange={(_, p) => setPage(p)}
            shape="rounded"
            color="primary"
          />
        </Stack>
      )}

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            setEditing(menu!.person);
            setMenu(null);
          }}
        >
          <ListItemIcon>
            <EditRounded fontSize="small" />
          </ListItemIcon>
          Editar
        </MenuItem>
        <MenuItem
          onClick={() => {
            setPasswordFor(menu!.person);
            setMenu(null);
          }}
        >
          <ListItemIcon>
            <KeyRounded fontSize="small" />
          </ListItemIcon>
          Definir senha
        </MenuItem>
        {hasModule('assessments') && menu?.person.role === 'student' && (
          <MenuItem
            onClick={() => {
              void navigate(`/assessments/students/${menu!.person.id}`);
              setMenu(null);
            }}
          >
            <ListItemIcon>
              <QueryStatsRounded fontSize="small" />
            </ListItemIcon>
            Ver desempenho
          </MenuItem>
        )}
        {menu &&
          menu.person.id !== me.id &&
          (isAdmin || (isManager && menu.person.role === 'student')) && (
            <MenuItem
              onClick={() => {
                const person = menu!.person;
                setMenu(null);
                startViewAs(person.id).catch((err: unknown) =>
                  notify(
                    err instanceof ApiError
                      ? err.message
                      : 'Não foi possível ver como esta pessoa.',
                    'error',
                  ),
                );
              }}
            >
              <ListItemIcon>
                <VisibilityRounded fontSize="small" />
              </ListItemIcon>
              Ver como esta pessoa
            </MenuItem>
          )}
        {menu?.person.id !== me.id && (
          <MenuItem
            onClick={() => {
              void toggleActive(menu!.person);
              setMenu(null);
            }}
          >
            <ListItemIcon>
              <KeyRounded fontSize="small" />
            </ListItemIcon>
            {menu?.person.isActive ? 'Desativar acesso' : 'Reativar acesso'}
          </MenuItem>
        )}
        {isAdmin && menu?.person.id !== me.id && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              void remove(menu!.person);
              setMenu(null);
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <DeleteOutlineRounded fontSize="small" />
            </ListItemIcon>
            Excluir
          </MenuItem>
        )}
      </Menu>

      <UserDialog
        target={editing}
        groups={groups.data ?? []}
        courses={courses.data ?? []}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          users.reload();
        }}
      />
      <PasswordDialog person={passwordFor} onClose={() => setPasswordFor(null)} />
      <CsvImportDialog
        open={importing}
        onClose={() => setImporting(false)}
        title="Importar pessoas"
        importPath="/users/import"
        templatePath="/users/import/template"
        templateName="modelo-pessoas.csv"
        excelTemplate
        onImported={() => {
          users.reload();
          groups.reload();
        }}
        previewColumns={[
          { key: 'name', label: 'Nome' },
          { key: 'email', label: 'E-mail institucional' },
          { key: 'phone', label: 'Telefone' },
          { key: 'personalEmail', label: 'E-mail pessoal' },
          { key: 'registration', label: 'Matrícula' },
          { key: 'role', label: 'Perfil', render: (v) => ROLE_LABEL[v as Role] ?? String(v) },
          { key: 'groups', label: 'Turmas' },
        ]}
        hints={
          <Typography variant="body2">
            As pessoas entram em <b>{institution?.name ?? 'escolha uma instituição no topo'}</b>.
            Colunas: <b>nome completo</b>, <b>email institucional</b>, <b>telefone</b>,{' '}
            <b>email pessoal</b> (só para contato), <b>matricula</b>, <b>papel</b> (aluno,
            professor, coordenador, admin; vazio = aluno), <b>curso</b> (nome, para alunos),{' '}
            <b>turmas</b> (código ou nome; várias separadas por |) e <b>senha</b> (opcional). Só
            nome e e-mail institucional são obrigatórios. Quem já existe (mesmo e-mail
            institucional) é atualizado, e célula vazia não apaga o que já está salvo; turmas que
            não existem são criadas. A pessoa entra com qualquer conta Google e confirma o e-mail
            institucional.
          </Typography>
        }
      />
    </>
  );
}

function UserDialog({
  target,
  groups,
  courses,
  onClose,
  onSaved,
}: {
  target: Person | 'new' | null;
  groups: Group[];
  courses: CourseRef[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { role: myRole, institutions, institution } = useSession();
  const notify = useNotify();
  const existing = target && target !== 'new' ? target : null;
  const [form, setForm] = useState({
    name: '',
    email: '',
    registration: '',
    role: 'student' as Role,
    groupIds: [] as string[],
    institutionIds: [] as string[],
    courseId: '',
    phone: '',
    personalEmail: '',
    password: '',
    isActive: true,
  });
  const [last, setLast] = useState<typeof target>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (target !== last) {
    setLast(target);
    setError(null);
    setForm(
      existing
        ? {
            name: existing.name ?? '',
            email: existing.email,
            registration: existing.registration ?? '',
            role: existing.role,
            groupIds: existing.groups.map((g) => g.id),
            institutionIds: existing.institutions.map((i) => i.id),
            courseId: existing.course?.id ?? '',
            phone: existing.phone ?? '',
            personalEmail: existing.personalEmail ?? '',
            password: '',
            isActive: existing.isActive,
          }
        : {
            name: '',
            email: '',
            registration: '',
            role: 'student',
            groupIds: [],
            institutionIds: institution ? [institution.id] : [],
            courseId: '',
            phone: '',
            personalEmail: '',
            password: '',
            isActive: true,
          },
    );
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return setError('Nome e e-mail são obrigatórios.');
    if (form.password && form.password.length < 8)
      return setError('A senha precisa ter pelo menos 8 caracteres.');
    if (form.role !== 'admin' && form.institutionIds.length === 0)
      return setError('Escolha a instituição.');
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        registration: form.registration.trim() || null,
        role: form.role,
        groupIds: form.groupIds,
        institutionIds: form.role === 'admin' ? [] : form.institutionIds,
        courseId: form.role === 'student' ? form.courseId || null : null,
        phone: form.phone.trim() || null,
        personalEmail: form.personalEmail.trim() || null,
      };
      if (existing) await updateUser(existing.id, { ...payload, isActive: form.isActive });
      else await createUser({ ...payload, password: form.password || null });
      notify(existing ? 'Dados atualizados.' : 'Pessoa cadastrada.', 'success');
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setSaving(false);
    }
  };

  const roles = MANAGEABLE[myRole];
  const locked = Boolean(existing && !roles.includes(existing.role));
  // Took over a pre-registration: signs in with their own Google account, not the institutional one.
  const ownGoogle = Boolean(
    existing?.institutionalEmailVerified &&
    existing.institutionalEmail &&
    existing.institutionalEmail !== existing.email,
  );
  // Only institutions you work at (admins: all). Links elsewhere are kept by the API.
  const mine = new Set(institutions.map((i) => i.id));
  const chosen = institutions.filter((i) => form.institutionIds.includes(i.id));
  const pickInstitutions = (next: InstitutionRef[]) =>
    setForm({
      ...form,
      institutionIds: [
        ...form.institutionIds.filter((id) => !mine.has(id)),
        ...next.map((i) => i.id),
      ],
    });

  return (
    <Dialog
      open={Boolean(target)}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { component: 'form', onSubmit: submit, noValidate: true } as object }}
    >
      <DialogTitle>{existing ? 'Editar pessoa' : 'Nova pessoa'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Nome completo"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoFocus
            required
          />
          <TextField
            label={ownGoogle ? 'E-mail de login (conta Google)' : 'E-mail institucional'}
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            helperText={
              ownGoogle
                ? `E-mail institucional confirmado: ${existing!.institutionalEmail}`
                : 'É com ele que a pessoa confirma o cadastro, mesmo entrando com outra conta Google.'
            }
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Telefone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="(11) 98765-4321"
              fullWidth
            />
            <TextField
              label="E-mail pessoal"
              type="email"
              value={form.personalEmail}
              onChange={(e) => setForm({ ...form, personalEmail: e.target.value })}
              helperText="Só para contato."
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Matrícula / RA"
              value={form.registration}
              onChange={(e) => setForm({ ...form, registration: e.target.value })}
              fullWidth
            />
            <TextField
              select
              label="Perfil"
              value={form.role}
              onChange={(e) => {
                const next = e.target.value as Role;
                setForm({
                  ...form,
                  role: next,
                  // Students have a single institution.
                  institutionIds:
                    next === 'student' ? form.institutionIds.slice(0, 1) : form.institutionIds,
                });
              }}
              fullWidth
              disabled={locked}
            >
              {(locked ? [existing!.role] : roles).map((r) => (
                <MenuItem key={r} value={r}>
                  {ROLE_LABEL[r]}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          {form.role === 'student' ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Instituição"
                value={chosen[0]?.id ?? ''}
                onChange={(e) =>
                  pickInstitutions(institutions.filter((i) => i.id === e.target.value))
                }
                fullWidth
                disabled={locked}
              >
                {institutions.map((i) => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Curso"
                value={form.courseId}
                onChange={(e) => setForm({ ...form, courseId: e.target.value })}
                fullWidth
                disabled={locked}
              >
                <MenuItem value="">—</MenuItem>
                {courses.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          ) : form.role !== 'admin' ? (
            <Autocomplete
              multiple
              options={institutions}
              value={chosen}
              onChange={(_, next) => pickInstitutions(next)}
              getOptionLabel={(i) => i.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              disabled={locked}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Instituições"
                  helperText={
                    form.role === 'coordinator'
                      ? 'Onde coordena: vê e gerencia os dados destas instituições.'
                      : 'Onde dá aula.'
                  }
                />
              )}
            />
          ) : null}
          <Autocomplete
            multiple
            options={groups}
            value={groups.filter((g) => form.groupIds.includes(g.id))}
            onChange={(_, next) => setForm({ ...form, groupIds: next.map((g) => g.id) })}
            getOptionLabel={(g) => g.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} label="Turmas" />}
          />
          {!existing && (
            <PasswordField
              label="Senha inicial (opcional)"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              helperText="Sem senha, a pessoa entra com Google ou cria uma em “Esqueci minha senha”."
            />
          )}
          {existing && (
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label="Acesso ativo"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" variant="contained" disabled={saving} sx={{ minWidth: 96 }}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PasswordDialog({ person, onClose }: { person: Person | null; onClose: () => void }) {
  const notify = useNotify();
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!person) return;
    setSaving(true);
    try {
      await setUserPassword(person.id, password);
      notify(`Senha definida para ${person.name ?? person.email}.`, 'success');
      setPassword('');
      onClose();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Não foi possível definir a senha.', 'error');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={Boolean(person)} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Definir senha</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography color="text.secondary">
            Para {person?.name ?? person?.email}. Combine a senha com a pessoa por um canal seguro.
          </Typography>
          <PasswordField
            label="Nova senha"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helperText="Mínimo de 8 caracteres"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={saving || password.length < 8}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
